import { useEffect, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../hooks/useAuth";
import { useToast } from "../../../hooks/useToast";
import DashboardLayout from "../../../components/feature/DashboardLayout";

interface Profile {
  id: string;
  role: string;
  full_name: string | null;
  email?: string;
  phone: string | null;
  created_at: string;
}

interface AppointmentWithNames {
  id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: string;
  client_name: string;
  caregiver_name: string;
}

interface CarePlanWithClient {
  id: string;
  plan_type: string;
  status: string;
  client_name: string;
  services: string[];
}

const navItems = [
  { label: "Dashboard", path: "/dashboard/admin", icon: "ri-dashboard-line" },
  { label: "Clients", path: "/dashboard/admin/clients", icon: "ri-user-line" },
  { label: "Caregivers", path: "/dashboard/admin/caregivers", icon: "ri-user-heart-line" },
  { label: "Appointments", path: "/dashboard/admin/appointments", icon: "ri-calendar-event-line" },
  { label: "Care Plans", path: "/dashboard/admin/care-plans", icon: "ri-file-list-3-line" },
  { label: "Settings", path: "/dashboard/admin/settings", icon: "ri-settings-3-line" },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [clients, setClients] = useState<Profile[]>([]);
  const [caregivers, setCaregivers] = useState<Profile[]>([]);
  const [appointments, setAppointments] = useState<AppointmentWithNames[]>([]);
  const [carePlans, setCarePlans] = useState<CarePlanWithClient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      // Clients
      const { data: cData } = await supabase
        .from("profiles")
        .select("*")
        .eq("role", "client")
        .order("created_at", { ascending: false })
        .limit(5);
      setClients(cData || []);

      // Caregivers
      const { data: gData } = await supabase
        .from("profiles")
        .select("*")
        .eq("role", "caregiver")
        .order("created_at", { ascending: false })
        .limit(5);
      setCaregivers(gData || []);

      // Appointments with names
      const { data: aData } = await supabase
        .from("appointments")
        .select("*, client:profiles!appointments_client_id_fkey(full_name), caregiver:profiles!appointments_caregiver_id_fkey(full_name)")
        .order("appointment_date", { ascending: false })
        .limit(5);
      const mappedAppts: AppointmentWithNames[] = (aData || []).map((a: any) => ({
        id: a.id,
        appointment_date: a.appointment_date,
        start_time: a.start_time,
        end_time: a.end_time,
        status: a.status,
        client_name: a.client?.full_name || "Unknown",
        caregiver_name: a.caregiver?.full_name || "Unassigned",
      }));
      setAppointments(mappedAppts);

      // Care plans with client names
      const { data: pData } = await supabase
        .from("care_plans")
        .select("*, client:profiles!care_plans_client_id_fkey(full_name)")
        .order("created_at", { ascending: false })
        .limit(5);
      const mappedPlans: CarePlanWithClient[] = (pData || []).map((p: any) => ({
        id: p.id,
        plan_type: p.plan_type,
        status: p.status,
        client_name: p.client?.full_name || "Unknown",
        services: p.services || [],
      }));
      setCarePlans(mappedPlans);

      setLoading(false);
    };
    fetchData();

    // Real-time subscriptions
    const apptsChannel = supabase
      .channel('appointments-admin-home')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'appointments',
      }, (payload) => {
        fetchData();
        if (payload.eventType === 'INSERT') showToast('New appointment created', 'success');
        if (payload.eventType === 'UPDATE') showToast('Appointment updated', 'info');
      })
      .subscribe();

    const profilesChannel = supabase
      .channel('profiles-admin-home')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'profiles',
      }, (payload) => {
        fetchData();
        if (payload.eventType === 'INSERT') showToast('New profile registered', 'success');
      })
      .subscribe();

    const plansChannel = supabase
      .channel('careplans-admin-home')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'care_plans',
      }, (payload) => {
        fetchData();
        if (payload.eventType === 'INSERT') showToast('New care plan created', 'success');
        if (payload.eventType === 'DELETE') showToast('Care plan deleted', 'error');
      })
      .subscribe();

    return () => {
      supabase.removeChannel(apptsChannel);
      supabase.removeChannel(profilesChannel);
      supabase.removeChannel(plansChannel);
    };
  }, [user, showToast]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const statCards = [
    { label: "Total Clients", value: clients.length, icon: "ri-user-line", color: "bg-blue-50 text-blue-600" },
    { label: "Caregivers", value: caregivers.length, icon: "ri-user-heart-line", color: "bg-brand-green-50 text-brand-green-600" },
    { label: "Appointments", value: appointments.length, icon: "ri-calendar-event-line", color: "bg-amber-50 text-amber-600" },
    { label: "Active Plans", value: carePlans.filter((p) => p.status === "active").length, icon: "ri-file-list-3-line", color: "bg-purple-50 text-purple-600" },
  ];

  return (
    <DashboardLayout navItems={navItems} title="Admin Portal" subtitle="Admin Dashboard">
      {loading ? (
        <div className="flex items-center gap-3 py-12">
          <div className="w-5 h-5 border-2 border-brand-green-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading dashboard...</span>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-brand-green-200 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${stat.icon} text-lg`} />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{stat.label}</span>
                </div>
                <p className="text-xl font-bold text-gray-900">{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Clients */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Clients</h2>
              {clients.length > 0 ? (
                <div className="space-y-3">
                  {clients.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl bg-soft-gray">
                      <div className="w-9 h-9 rounded-full bg-brand-green-100 flex items-center justify-center shrink-0">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-user-line text-brand-green-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.full_name || "Unnamed Client"}</p>
                        <p className="text-xs text-gray-500">{c.phone || "No phone"}</p>
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(c.created_at)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center">No clients registered yet.</p>
              )}
            </div>

            {/* Recent Appointments */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Appointments</h2>
              {appointments.length > 0 ? (
                <div className="space-y-3">
                  {appointments.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-soft-gray">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-calendar-line text-blue-600" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{a.client_name}</p>
                        <p className="text-xs text-gray-500">
                          {formatDate(a.appointment_date)} · {a.start_time}–{a.end_time}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        a.status === "scheduled" ? "bg-blue-50 text-blue-700" :
                        a.status === "completed" ? "bg-green-50 text-green-700" :
                        "bg-red-50 text-red-700"
                      }`}>
                        {a.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center">No appointments scheduled yet.</p>
              )}
            </div>
          </div>

          {/* Care Plans */}
          <div className="mt-6 bg-white rounded-2xl p-6 border border-gray-100">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Recent Care Plans</h2>
            {carePlans.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {carePlans.map((p) => (
                  <div key={p.id} className="p-4 rounded-xl bg-soft-gray border border-gray-100 hover:border-brand-green-200 transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">{p.plan_type}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        p.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"
                      }`}>
                        {p.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{p.client_name}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {p.services.slice(0, 3).map((s) => (
                        <span key={s} className="px-2 py-0.5 rounded text-[10px] font-medium bg-white text-gray-600 border border-gray-100">
                          {s}
                        </span>
                      ))}
                      {p.services.length > 3 && (
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-white text-gray-400 border border-gray-100">
                          +{p.services.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 py-4 text-center">No care plans created yet.</p>
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
